const IN_PROGRESS = "IN_PROGRESS";

const parseStats = (stats) => {
  if (!stats) return {};
  if (typeof stats === "string") {
    try {
      return JSON.parse(stats);
    } catch {
      return {};
    }
  }
  return typeof stats === "object" ? stats : {};
};

const isInProgressItem = (item) =>
  item &&
  item.status === IN_PROGRESS &&
  typeof item.progress === "number" &&
  item.progress > 0 &&
  !!item.id;

export const getInProgressStories = (stats) => {
  const parsed = parseStats(stats);
  const stories = Array.isArray(parsed.stories) ? parsed.stories : [];
  return stories.filter(isInProgressItem);
};

export const getInProgressStorybooks = (stats) => {
  const parsed = parseStats(stats);
  const storybooks = Array.isArray(parsed.storybooks) ? parsed.storybooks : [];
  return storybooks.filter(isInProgressItem);
};

export const getInProgressPlaybackItems = (stats) => {
  const stories = getInProgressStories(stats).map((item) => ({
    id: item.id,
    type: "story",
    progress: item.progress,
  }));

  const storybooks = getInProgressStorybooks(stats).map((item) => ({
    id: item.id,
    type: "storybook",
    progress: item.progress,
  }));

  return [...stories, ...storybooks];
};

export const getStoryProgressPercent = (story, progressSeconds) => {
  const maybeDuration = getStoryDurationSeconds(story);

  if (typeof maybeDuration === "number" && maybeDuration > 0) {
    return Math.max(0, Math.min(100, (progressSeconds / maybeDuration) * 100));
  }

  // If no duration metadata exists, treat 0..100 as percent-like fallback.
  if (progressSeconds <= 100) {
    return Math.max(0, Math.min(100, progressSeconds));
  }

  // Unknown duration and progress > 100 seconds: show partial bar instead of full.
  return 65;
};

export const getStorybookProgressPercent = (audiobook, pageIndex) => {
  const pageCount = Array.isArray(audiobook?.pages) ? audiobook.pages.length : 0;
  if (pageCount <= 0) return 0;
  return Math.max(0, Math.min(100, (pageIndex / Math.max(1, pageCount - 1)) * 100));
};

export const getStoryDurationSeconds = (story) => {
  const maybeDuration =
    story?.duration_seconds ||
    story?.duration ||
    story?.video_duration_seconds ||
    story?.video_duration;
  return typeof maybeDuration === "number" && maybeDuration > 0 ? maybeDuration : 0;
};

export const normalizeStoryResumeSeconds = (progressValue, story) => {
  const progress = Number(progressValue);
  if (!Number.isFinite(progress) || progress <= 0) return 0;

  const duration = getStoryDurationSeconds(story);

  // Some clients store a fraction (0..1) instead of absolute seconds.
  if (duration > 0 && progress > 0 && progress < 1) {
    return progress * duration;
  }

  if (duration > 0) {
    return Math.max(0, Math.min(progress, duration));
  }

  return Math.max(0, progress);
};

export const clampStoryResumeSeconds = (seconds, duration) => {
  const value = Number(seconds);
  if (!Number.isFinite(value)) return 0;
  if (!Number.isFinite(duration) || duration <= 0) return Math.max(0, value);
  return Math.max(0, Math.min(value, duration));
};

export const clampStorybookResumePage = (pageValue, totalPages) => {
  const page = Math.floor(Number(pageValue));
  const safePage = Number.isFinite(page) ? page : 0;
  const maxPage = Math.max(0, Number(totalPages || 0) - 1);
  return Math.max(0, Math.min(safePage, maxPage));
};
